/**
 * GrantMatches — `/grants/matches` Top Matches route (G2A CP3).
 *
 * - Premium: full sorted list of computed matches, filtered by min_tier.
 * - Free: inline upgrade CTA on-route (per CP3 §3). Not a modal, not a
 *   redirect — the route is a legit member destination that shows a
 *   friendly upsell when tier gates fire.
 *
 * The backend `GET /api/grants/matches/top` returns HTTP 402 with
 * `code: "match_top_page_premium_only"` for Free users; this page
 * catches that and renders the upgrade block.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  ArrowUpRight,
  Filter,
  Loader2,
  Target,
} from "lucide-react";
import { api } from "@/api/client";
import GrantShell from "@/components/grants/GrantShell";
import MatchTierChip from "@/components/grants/MatchTierChip";
import EligibilityBadge from "@/components/grants/EligibilityBadge";
import PremiumUpgradePanel from "@/components/grants/PremiumUpgradePanel";
import { PageHeader } from "@/design-system/components/PageHeader";
import { Card } from "@/design-system/components/Card";
import { Heading } from "@/design-system/components/Heading";

const TIER_FILTER_OPTIONS = [
  { value: "", label: "All tiers" },
  { value: "poor_match", label: "Poor Match and above" },
  { value: "weak_match", label: "Weak Match and above" },
  { value: "fair_match", label: "Fair Match and above" },
  { value: "good_match", label: "Good Match and above" },
  { value: "strong_match", label: "Strong Match only" },
];

function MatchRow({ item }) {
  const { grant, match } = item;
  return (
    <Card
      data-testid={`top-match-row-${grant.id}`}
      className="rounded-[var(--ds-radius-card)]"
    >
      <div className="px-4 pt-4 pb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div
            className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-semibold"
            style={{ color: "var(--ds-color-ink-subtle)" }}
          >
            <Building2 className="w-3 h-3" aria-hidden="true" />
            <span className="truncate">{grant.agency}</span>
          </div>
          <Heading
            level={3}
            size="lg"
            weight="semibold"
            className="mt-1 leading-snug"
          >
            <Link
              to={`/grants/${grant.id}`}
              data-testid={`top-match-title-link-${grant.id}`}
              className="hover:underline"
              style={{ color: "var(--ds-color-ink)" }}
            >
              {grant.title}
            </Link>
          </Heading>
        </div>
        {/* Discipline 2: match chip and eligibility badge are STILL
            visually distinct here — never merged. */}
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <MatchTierChip
            tier={match.match_tier}
            score={match.match_score}
            size="md"
            testId={`top-match-tier-chip-${grant.id}`}
          />
          <EligibilityBadge
            status={match.eligibility_status}
            size="sm"
            testId={`top-match-eligibility-badge-${grant.id}`}
          />
        </div>
      </div>

      {match.narrative_strengths?.length > 0 && (
        <div className="px-4 mt-3">
          <p
            className="text-[11px] uppercase tracking-[0.16em] font-semibold"
            style={{ color: "var(--ds-color-ink-subtle)" }}
          >
            Where the profile appears to align
          </p>
          <ul
            className="mt-1.5 space-y-1 text-[13px] leading-snug"
            style={{ color: "var(--ds-color-ink)" }}
          >
            {match.narrative_strengths.slice(0, 2).map((s, i) => (
              <li key={i} className="flex gap-2">
                <span
                  className="shrink-0"
                  style={{ color: "var(--ds-color-accent)" }}
                >
                  •
                </span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {match.narrative_gaps?.length > 0 && (
        <div className="px-4 mt-3">
          <p
            className="text-[11px] uppercase tracking-[0.16em] font-semibold"
            style={{ color: "var(--ds-color-ink-subtle)" }}
          >
            Considerations to strengthen
          </p>
          <ul
            className="mt-1.5 space-y-1 text-[13px] leading-snug"
            style={{ color: "var(--ds-color-ink)" }}
          >
            {match.narrative_gaps.slice(0, 2).map((g, i) => (
              <li key={i} className="flex gap-2">
                <span
                  className="shrink-0"
                  style={{ color: "var(--ds-color-accent)" }}
                >
                  •
                </span>
                <span>{g}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div
        className="mt-4 flex items-center justify-between border-t px-4 py-2.5"
        style={{ borderColor: "var(--ds-color-border)" }}
      >
        <Link
          to={`/grants/${grant.id}`}
          data-testid={`top-match-view-link-${grant.id}`}
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold hover:underline"
          style={{ color: "var(--ds-color-accent)" }}
        >
          View grant
          <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
        </Link>
        {match.llm_fail_reason && (
          <span
            className="text-[11px]"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            Deterministic-only fallback ({match.llm_fail_reason.replace(/_/g, " ")})
          </span>
        )}
      </div>
    </Card>
  );
}

export default function GrantMatches() {
  const [state, setState] = useState({
    loading: true,
    isPremiumUpsell: false,
    items: null,
    error: null,
  });
  const [minTier, setMinTier] = useState("");

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    const params = {};
    if (minTier) params.min_tier = minTier;
    api
      .get("/grants/matches/top", { params })
      .then((res) => {
        if (!cancelled)
          setState({
            loading: false,
            isPremiumUpsell: false,
            items: res.data.items || [],
            error: null,
          });
      })
      .catch((err) => {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        if (
          err?.response?.status === 402 &&
          detail?.code === "match_top_page_premium_only"
        ) {
          setState({
            loading: false,
            isPremiumUpsell: true,
            items: null,
            error: null,
          });
          return;
        }
        setState({
          loading: false,
          isPremiumUpsell: false,
          items: null,
          error: detail?.message || err?.message || "Could not load Top Matches.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [minTier]);

  return (
    <GrantShell testId="grant-matches-root">
      <PageHeader
        eyebrow="Grant Navigator"
        title="Top Matches"
        description="Grants ranked by AI match score, refreshed as you compute new matches or update your grant profile. Score is an educational estimate — sponsors make final funding decisions."
        data-testid="grant-matches-header"
      />

      {state.isPremiumUpsell && <PremiumUpgradePanel scope="matches" />}

      {!state.isPremiumUpsell && (
        <section className="mt-2 flex items-center gap-3">
          <label className="relative">
            <Filter
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
              style={{ color: "var(--ds-color-ink-subtle)" }}
              aria-hidden="true"
            />
            <select
              data-testid="top-matches-tier-filter"
              value={minTier}
              onChange={(e) => setMinTier(e.target.value)}
              className="rounded-[var(--ds-radius-field)] pl-10 pr-8 h-10 text-[14px] focus:outline-none appearance-none"
              style={{
                background: "var(--ds-color-surface)",
                border: "1px solid var(--ds-color-border)",
                color: "var(--ds-color-ink)",
              }}
            >
              {TIER_FILTER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </section>
      )}

      {!state.isPremiumUpsell && state.loading && (
        <div
          data-testid="top-matches-loading"
          className="mt-8 flex items-center gap-2 text-[13px]"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          <Loader2 className="w-4 h-4 animate-spin" /> Loading top matches…
        </div>
      )}

      {!state.isPremiumUpsell && !state.loading && state.error && (
        <div
          data-testid="top-matches-error"
          className="mt-8 rounded-[var(--ds-radius-card)] p-6"
          style={{
            background: "var(--ds-color-surface)",
            border: "1px solid var(--ds-color-border)",
          }}
        >
          <p className="text-[13px]" style={{ color: "var(--ds-color-ink)" }}>
            {state.error}
          </p>
        </div>
      )}

      {!state.isPremiumUpsell &&
        !state.loading &&
        !state.error &&
        state.items &&
        state.items.length === 0 && (
          <div
            data-testid="top-matches-empty"
            className="mt-8 rounded-[var(--ds-radius-card)] p-10 text-center"
            style={{
              background: "var(--ds-color-surface)",
              border: "1px dashed var(--ds-color-border-strong)",
            }}
          >
            <Target
              className="mx-auto w-5 h-5"
              style={{ color: "var(--ds-color-accent)" }}
              aria-hidden="true"
            />
            <p
              className="mt-3 text-[14px] font-semibold"
              style={{ color: "var(--ds-color-ink)" }}
            >
              No computed matches yet.
            </p>
            <p
              className="mt-2 text-[13px]"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              Head to the Grant Finder and click "Compute AI Match Analysis" on
              any grant to populate your Top Matches list.
            </p>
            <Link
              to="/grants"
              data-testid="top-matches-empty-cta"
              className="mt-5 inline-flex items-center gap-2 rounded-[var(--ds-radius-md)] px-4 py-2 text-[13px] font-semibold transition-colors"
              style={{
                background: "var(--ds-color-accent)",
                color: "var(--ds-color-accent-ink)",
                transitionDuration: "var(--ds-duration-fast)",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "var(--ds-color-accent-strong)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "var(--ds-color-accent)")
              }
            >
              Open Grant Finder <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

      {!state.isPremiumUpsell &&
        !state.loading &&
        !state.error &&
        state.items &&
        state.items.length > 0 && (
          <div
            data-testid="top-matches-list"
            className="mt-6 grid grid-cols-1 gap-4"
          >
            {state.items.map((it) => (
              <MatchRow key={it.grant.id} item={it} />
            ))}
          </div>
        )}
    </GrantShell>
  );
}
